"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, FormStatus, SubmitButton } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { dispatchMfaChanged } from "@/lib/api/mfa";
import { cUserAccountsTranslations } from "./CUserAccounts.i18n";
import { TabHeader } from "./TabHeader";
import { MfaCodeInput } from "./MfaCodeInput";
import cardStyles from "./CUserAccounts.module.css";
import styles from "./ProfileTabSecurity.module.css";

/**
 * ProfileTabSecurity - karta "Bezpieczeństwo" w panelu klienta.
 *
 * Dwa warianty UI na podstawie `mfaStatus.enabled`:
 *
 *  1. **Disabled -> setup panel:**
 *     - Krok 1: klik "Wygeneruj klucz" -> POST /api/iiapi/user/mfa/setup ->
 *       backend zwraca `{ secret, qr_data_uri, otpauth_url }`. Secret NIE
 *       jest zapisany w bazie do momentu udanego /enable.
 *     - Krok 2: user skanuje QR w aplikacji (Google/Microsoft Authenticator).
 *     - Krok 3: 6-cyfrowy kod z aplikacji -> POST /api/iiapi/user/mfa/enable
 *       z `{ secret, code }`. Backend weryfikuje kod, zapisuje encrypted
 *       secret + `enabled_tfa=1`. Auto-submit gdy user wpisze wszystkie 6 cyfr.
 *
 *  2. **Enabled -> disable panel:**
 *     - Info "MFA aktywne" + input hasła (elevated auth) + przycisk "Wyłącz".
 *     - POST /api/iiapi/user/mfa/disable z `{ password }`. Backend weryfikuje
 *       hasło i kasuje `secret_tfa` + `enabled_tfa`.
 *
 * **Widocznosc karty** sterowana przez `content.mfa_available` (z backend
 * `getApiContent` na podstawie rozszerzenia `imperial_TFA_login`). Karta
 * NIE jest renderowana gdy rozszerzenie wylaczone - guard w `ProfileTabs.tsx`.
 */

type MfaStatus = { available: boolean; enabled: boolean };

type SetupData = { secret: string; qr_data_uri: string; otpauth_url: string };

export function ProfileTabSecurity({ mfaOption }: { mfaOption: "disabled" | "optional" | "required" }) {
  const t = useT(cUserAccountsTranslations);
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/iiapi/user/mfa/status", { method: "GET" });
        const body = (await res.json().catch(() => null)) as
          | { ok?: boolean; data?: MfaStatus; message?: string }
          | null;
        if (cancelled) return;
        if (!res.ok || !body?.data) {
          setLoadError(body?.message ?? t.security.loadFailed);
          return;
        }
        setStatus(body.data);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t.security.loadFailed]);

  //Zmiana stanu MFA - update lokalny + broadcast do subskrybentów (MfaGuard
  //nasłuchuje żeby schować banner "wymagane MFA" po tym jak user zapisze
  //konfigurację). Bez tego banner wisiał do momentu refresh strony.
  const onStatusChange = (next: MfaStatus) => {
    setStatus(next);
    dispatchMfaChanged();
  };

  return (
    <>
      <TabHeader icon="lock" title={t.security.title} />
      {loadError && <FormStatus variant="error">{loadError}</FormStatus>}
      {!status && !loadError && <p>{t.security.loading}</p>}
      {status && !status.enabled && <SecuritySetupPanel onEnabled={() => onStatusChange({ ...status, enabled: true })} />}
      {status && status.enabled && mfaOption === "required" && <SecurityRequiredStatusPanel />}
      {status && status.enabled && mfaOption !== "required" && <SecurityDisablePanel onDisabled={() => onStatusChange({ ...status, enabled: false })} />}
    </>
  );
}

/* ─── Setup panel (MFA wylaczone -> user chce wlaczyc) ───────────────── */

const enableSchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/, "Kod musi zawierać dokładnie 6 cyfr."),
});
type EnableValues = z.infer<typeof enableSchema>;

function SecuritySetupPanel({ onEnabled }: { onEnabled: () => void }) {
  const t = useT(cUserAccountsTranslations);
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  const {
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<EnableValues>({
    resolver: zodResolver(enableSchema),
    defaultValues: { code: "" },
  });
  const code = watch("code");
  const [topError, setTopError] = useState<string | null>(null);

  const requestSetup = async () => {
    setSetupBusy(true);
    setSetupError(null);
    setTopError(null);
    try {
      const res = await fetch("/api/iiapi/user/mfa/setup", { method: "POST" });
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean; data?: SetupData; message?: string }
        | null;
      if (!res.ok || !body?.data) {
        setSetupError(body?.message ?? t.security.setupFailed);
        return;
      }
      setSetup(body.data);
    } catch (err) {
      setSetupError(err instanceof Error ? err.message : String(err));
    } finally {
      setSetupBusy(false);
    }
  };

  const onSubmit = async (values: EnableValues) => {
    if (!setup) return;
    setTopError(null);
    try {
      const res = await fetch("/api/iiapi/user/mfa/enable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secret: setup.secret, code: values.code }),
      });
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean; message?: string; code?: number }
        | null;
      if (!res.ok) {
        //Rate limit -> user musi wygenerowac nowy secret (setup na nowo).
        if (body?.code === 5006) {
          setSetup(null);
          setValue("code", "");
          setTopError(body.message ?? t.security.rateLimitExceeded);
          return;
        }
        //Zly kod - inline error na inpucie.
        if (body?.code === 5005 || body?.code === 5004) {
          setError("code", { type: "server", message: body?.message ?? t.security.invalidCode });
          return;
        }
        setTopError(body?.message ?? t.security.enableFailed);
        return;
      }
      onEnabled();
    } catch (err) {
      setTopError(err instanceof Error ? err.message : String(err));
    }
  };

  //Auto-submit gdy user wpisze wszystkie 6 cyfr.
  const handleCodeComplete = () => {
    void handleSubmit(onSubmit)();
  };

  if (!setup) {
    return (
      <div className={cardStyles.form}>
        <div className={styles.infoBox}>
          <Icon name="info" size={16} className={styles.infoBoxIcon} />
          <div className={styles.infoBoxContent}>
            <p>{t.security.setupIntro}</p>
            <p className={styles.appsList}>
              <b>Google Authenticator</b> &middot; <b>Microsoft Authenticator</b>
            </p>
          </div>
        </div>
        {setupError && <FormStatus variant="error">{setupError}</FormStatus>}
        <div className={cardStyles.profileFormActions}>
          <SubmitButton loading={setupBusy} onClick={requestSetup} type="button">
            {t.security.actionSetupStart}
          </SubmitButton>
        </div>
      </div>
    );
  }

  return (
    <form className={cardStyles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Krok 1: skan QR */}
      <div>
        <div className={styles.stepHeader}>
          <span className={styles.stepNumber}>1</span>
          <h3 className={styles.stepTitle}>{t.security.step1Title}</h3>
        </div>
        <p className={styles.stepDescription}>{t.security.step1Description}</p>
        <div className={styles.qrContainer}>
          {/* QR wygenerowany server-side jako PNG data URI (BaconQrCodeProvider). */}
          <img src={setup.qr_data_uri} alt={t.security.qrAlt} className={styles.qrImage} />
          <p className={styles.qrSecretLabel}>{t.security.qrManualEntry}</p>
          <code className={styles.qrSecretCode}>{setup.secret}</code>
        </div>
      </div>

      {/* Krok 2: kod z aplikacji */}
      <div>
        <div className={styles.stepHeader}>
          <span className={styles.stepNumber}>2</span>
          <h3 className={styles.stepTitle}>{t.security.step2Title}</h3>
        </div>
        <p className={styles.stepDescription}>{t.security.step2Description}</p>
        <MfaCodeInput
          value={code}
          onChange={(next) => setValue("code", next, { shouldValidate: false })}
          onComplete={handleCodeComplete}
          disabled={isSubmitting}
          hasError={Boolean(errors.code)}
          autoFocus
        />
        {errors.code?.message && <FormStatus variant="error">{errors.code.message}</FormStatus>}
      </div>

      {topError && <FormStatus variant="error">{topError}</FormStatus>}

      <div className={cardStyles.profileFormActions}>
        <SubmitButton loading={isSubmitting} disabled={code.length !== 6}>
          {t.security.actionEnable}
        </SubmitButton>
      </div>
    </form>
  );
}

/* ─── Disable panel (MFA wlaczone -> user chce wylaczyc) ─────────────── */

const disableSchema = z.object({
  password: z.string().min(1, "Podaj aktualne hasło."),
});
type DisableValues = z.infer<typeof disableSchema>;

function SecurityDisablePanel({ onDisabled }: { onDisabled: () => void }) {
  const t = useT(cUserAccountsTranslations);
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DisableValues>({
    resolver: zodResolver(disableSchema),
    mode: "onTouched",
    defaultValues: { password: "" },
  });
  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (values: DisableValues) => {
    setTopError(null);
    setSuccess(null);
    try {
      const res = await fetch("/api/iiapi/user/mfa/disable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean; message?: string; code?: number }
        | null;
      if (!res.ok) {
        if (body?.code === 5004) {
          setError("password", { type: "server", message: body.message ?? t.security.invalidPassword });
          return;
        }
        setTopError(body?.message ?? t.security.disableFailed);
        return;
      }
      setSuccess(t.security.disableSuccess);
      reset({ password: "" });
      onDisabled();
    } catch (err) {
      setTopError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <form className={cardStyles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.enabledStatus}>
        <Icon name="lock" size={24} className={styles.enabledStatusIcon} />
        <div className={styles.enabledStatusText}>
          <p><b>{t.security.enabledTitle}</b></p>
          <p>{t.security.enabledDescription}</p>
        </div>
      </div>

      <Field label={t.security.disablePasswordLabel} required error={errors.password?.message}>
        <Input
          type="password"
          autoComplete="current-password"
          disabled={isSubmitting}
          {...register("password")}
        />
      </Field>

      {topError && <FormStatus variant="error">{topError}</FormStatus>}
      {success && <FormStatus variant="success">{success}</FormStatus>}

      <div className={cardStyles.profileFormActions}>
        <SubmitButton loading={isSubmitting}>{t.security.actionDisable}</SubmitButton>
      </div>
    </form>
  );
}

/* ─── Required-status panel (MFA wlaczone + grupa wymaga -> tylko info, brak disable) ─── */

//Analog PHP admin: gdy 2fa_option='2' i user ma skonfigurowane MFA - backend
//`disableMfa` blokuje wylaczenie (`is2FARequiredByGroup`). Zeby uniknac UX-owego
//konfliktu (przycisk widoczny -> klik -> policy-block popup) nie renderujemy
//w ogole formularza disable; pokazujemy tylko status + info o wymuszeniu przez grupe.
function SecurityRequiredStatusPanel() {
  const t = useT(cUserAccountsTranslations);
  return (
    <div className={cardStyles.form}>
      <div className={styles.enabledStatus}>
        <Icon name="lock" size={24} className={styles.enabledStatusIcon} />
        <div className={styles.enabledStatusText}>
          <p><b>{t.security.enabledTitle}</b></p>
          <p>{t.security.enabledDescription}</p>
        </div>
      </div>
      <FormStatus variant="info">{t.security.requiredByGroupConfigured}</FormStatus>
    </div>
  );
}
